import { getString } from "@/common/uistringlabels/ui-string-utils";
import type { PickupDeliveryMethod } from "@/pages/Replacement/replacement.types";
import type {
  ShipmentDeliveryAddress,
  ShipmentDeliveryOption,
} from "@/services/shipment-options/shipment-options.types";
import { presentAddressValue } from "@/services/shipment-options/shipment-address.mapper";
import type { DeliveryChoices, PickupBuilding } from "./choose-delivery.types";

const PICKUP_METHODS: readonly string[] = ["BSC", "IT_OFFICE"];

const isPickupMethod = (method: string): method is PickupDeliveryMethod =>
  PICKUP_METHODS.includes(method);

/** One-line pickup address: street lines, city, "state zip" and country, skipping absent parts. */
const formatPickupAddress = (address?: ShipmentDeliveryAddress): string | undefined => {
  if (!address) return undefined;
  const stateAndZip = [presentAddressValue(address.state), presentAddressValue(address.zip)]
    .filter(Boolean)
    .join(" ");
  const parts = [address.line1, address.line2, address.city, stateAndZip, address.country]
    .map(presentAddressValue)
    .filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : undefined;
};

/**
 * A BSC or IT office option becomes a pickup building labelled "Name (CODE) - Method", so two methods
 * in the same building can be told apart. Options without a building code cannot be selected and are dropped.
 */
const toPickupBuilding = (option: ShipmentDeliveryOption): PickupBuilding | undefined => {
  if (!isPickupMethod(option.deliveryMethod) || !option.buildingCode) return undefined;
  const name = option.location?.name;
  const buildingName = name ? `${name} (${option.buildingCode})` : option.buildingCode;
  const methodName = getString(`replacement.chooseDelivery.pickupMethods.${option.deliveryMethod}`);
  const address = formatPickupAddress(option.address);
  const deliveryInstructions = presentAddressValue(option.location?.deliveryInstructions);
  return {
    deliveryMethod: option.deliveryMethod,
    buildingCode: option.buildingCode,
    label: `${buildingName} - ${methodName}`,
    ...(address ? { address } : {}),
    ...(deliveryInstructions ? { deliveryInstructions } : {}),
  };
};

/** Turns ShipmentHound's options into what the step offers: pickup buildings (BSC and IT office) and whether Ship (STH) is available. */
export const toDeliveryChoices = (options: ShipmentDeliveryOption[]): DeliveryChoices => ({
  pickupBuildings: options
    .map(toPickupBuilding)
    .filter((building): building is PickupBuilding => building !== undefined),
  isShipAvailable: options.some((option) => option.deliveryMethod === "STH"),
});

/** The option description shown under a building: its address, then its pickup instructions. */
export const describePickupBuilding = (building: PickupBuilding): string =>
  [building.address, building.deliveryInstructions].filter(Boolean).join(". ");

/** Building search: case-insensitive match on the label, which holds the name and code; a blank search lists every building. */
export const filterPickupBuildings = (
  buildings: PickupBuilding[],
  query: string
): PickupBuilding[] => {
  const term = query.trim().toLowerCase();
  return term
    ? buildings.filter((building) => building.label.toLowerCase().includes(term))
    : buildings;
};
